import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {CardWidget} from 'shells/card/card-widget/card-widget';
// @ts-expect-error - imported from .js/.jsx file
import {StepIcon} from 'shells/icon/svg/step-icon';

import type {
    LeadStepFragment as Step,
    StepUpdateInput,
    SkipStepInput,
    StepUnskipInput,
} from 'nutshell-graphql-types';

import {TaskList} from '../../../core-entity/widgets/tasks-widget/components/task-list';

import {useDelayStepForLead} from './graphql/hooks/use-delay-step-for-lead';
import {useGetStepsByStage} from './graphql/hooks/use-get-steps-by-stage';
import {useSkipStepForLead} from './graphql/hooks/use-skip-step-for-lead';
import {useUnskipStepForLead} from './graphql/hooks/use-unskip-step-for-lead';
import {useUpdateStepForLead} from './graphql/hooks/use-update-step-for-lead';

type Props = {
    leadId: string;
    selectedStageId: string;
};

export function StageTaskSection(props: Props) {
    const [error, setError] = React.useState<string | null | undefined>(null);

    const {stepsByStage, isLoading} = useGetStepsByStage(props.leadId);
    const activeStepsByStage =
        stepsByStage && stepsByStage.find((stage) => stage.stage.id === props.selectedStageId);
    // Cast to task array because flow doesn't understand how to convert from
    // the type queried here to the Task union type
    const steps: Step[] =
        activeStepsByStage && activeStepsByStage.steps ? (activeStepsByStage.steps as any) : [];

    const {updateStepForLead} = useUpdateStepForLead();
    const {skipStepForLead} = useSkipStepForLead();
    const {delayStepForLead} = useDelayStepForLead();
    const {unskipStepForLead} = useUnskipStepForLead();

    const handleUpdateStep = React.useCallback(
        (input: StepUpdateInput) => {
            return updateStepForLead(input).catch((e) => {
                setError(safelyGetGraphQLErrorMessage(e));
            });
        },
        [updateStepForLead]
    );

    const handleSkipStep = React.useCallback(
        (input: SkipStepInput) => {
            return skipStepForLead(input).catch((e) => {
                setError(safelyGetGraphQLErrorMessage(e));
            });
        },
        [skipStepForLead]
    );

    const handleUnskipStep = React.useCallback(
        (input: StepUnskipInput) => {
            return unskipStepForLead(input).catch((e) => {
                setError(safelyGetGraphQLErrorMessage(e));
            });
        },
        [unskipStepForLead]
    );

    const stepMutations = React.useMemo(
        () => ({
            onUpdateStep: handleUpdateStep,
            onSkipStep: handleSkipStep,
            onDelayStep: delayStepForLead,
            onUnskipStep: handleUnskipStep,
        }),
        [handleUpdateStep, handleSkipStep, delayStepForLead, handleUnskipStep]
    );

    return (
        <CardWidget
            title='Stage tasks'
            isLoading={isLoading}
            errorProps={{
                error,
                setError,
            }}
            emptyStateProps={{
                isEmpty: steps.length === 0,
                text: 'No tasks for this stage',
                icon: StepIcon,
                iconSize: 18,
            }}
            hasBorder={false}
        >
            <TaskList setError={setError} tasks={steps} stepMutations={stepMutations} />
        </CardWidget>
    );
}
